import React, { useState } from 'react';
import axios from '../axios-config';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, AlertTriangle, CheckCircle2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminFYDrill() {
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmRun, setConfirmRun] = useState(false);

  const runClose = async () => {
    if (!confirmRun) { setConfirmRun(true); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/financial-year/close`, {});
      setResult(data);
      setConfirmRun(false);
      toast.success(`FY ${data.closing_fy} closed → archived ${data.archived_count}, carried forward ${data.carried_forward_count}`);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('FY close failed', e);
      toast.error(e.response?.data?.detail || 'Failed to run FY closure');
    }
    finally { setBusy(false); }
  };

  const resetDemo = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/demo/reset`, {});
      setResult(null);
      setConfirmRun(false);
      toast.success('Demo data restored — drill reset complete');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Demo reset failed', e);
      toast.error(e.response?.data?.detail || 'Reset failed (admin only)');
    }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-navy text-white">
        <nav className="container mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="text-white/70 hover:text-white inline-flex items-center" data-testid="fy-back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </button>
          <div className="font-bold flex items-center gap-2"><RefreshCw className="h-5 w-5 text-gold" /> FY Closure Drill</div>
          <div />
        </nav>
      </header>

      <div className="container mx-auto px-4 sm:px-6 py-6 max-w-3xl">
        <div className="bg-card border-2 border-orange/40 rounded-lg p-5 mb-5" data-testid="fy-drill-intro">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-orange mt-0.5" />
            <div>
              <div className="font-bold text-navy mb-1">Sandbox Drill</div>
              <p className="text-xs text-muted-foreground">Use this on the demo dataset to safely verify FY closure behaviour before running it on real customers. Archives all <strong>closed</strong> tenders and carries forward all <strong>active</strong> tenders (with full audit trail) to the next FY. The "Reset Drill" button restores the canonical 4 demo tenders.</p>
            </div>
          </div>
        </div>

        {confirmRun && (
          <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4 mb-5" data-testid="fy-confirm-banner">
            <div className="text-sm text-navy">
              <strong>Confirm:</strong> Run Financial Year Closure now? Closed tenders will be archived; active tenders will be carried forward to the next FY. Click <em>Run FY Closure</em> again to proceed.
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-3 mb-5">
          <button onClick={runClose} disabled={busy} className="px-5 py-2.5 rounded bg-orange text-white text-sm font-bold disabled:opacity-40 inline-flex items-center" data-testid="fy-run-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${busy ? 'animate-spin' : ''}`} /> {confirmRun ? 'Confirm — Run FY Closure' : 'Run FY Closure'}
          </button>
          <button onClick={resetDemo} disabled={busy} className="px-5 py-2.5 rounded border-2 border-navy text-navy text-sm font-bold disabled:opacity-40 inline-flex items-center hover:bg-navy hover:text-white transition-colors" data-testid="fy-reset-btn">
            <RotateCcw className="h-4 w-4 mr-2" /> Reset Drill (Restore Demo Data)
          </button>
        </div>

        {result && (
          <div className="bg-card border border-border rounded-lg p-5" data-testid="fy-drill-result">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <div className="font-bold text-navy">Closure Complete</div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <Field label="Closing FY" value={result.closing_fy} testid="fy-closing" />
              <Field label="Next FY" value={result.next_fy} testid="fy-next" />
              <Field label="Tenders Archived" value={result.archived_count} testid="fy-archived" />
              <Field label="Carried Forward" value={result.carried_forward_count} testid="fy-carried" />
            </div>
            {result.carried_forward_ids?.length > 0 && (
              <div className="mt-4">
                <div className="text-xs font-bold text-navy mb-1">Carried-forward tender IDs</div>
                <div className="text-[11px] font-mono text-muted-foreground space-y-0.5" data-testid="fy-cf-list">
                  {result.carried_forward_ids.map(id => <div key={id}>{id}</div>)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, value, testid }) => (
  <div className="p-3 rounded border border-border" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground">{label}</div>
    <div className="text-base font-bold text-navy">{value ?? 0}</div>
  </div>
);
